import {
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Text,
  useBreakpointValue,
  Flex,
  Box,
  Divider,
  Breadcrumb,
  BreadcrumbItem,
  Icon,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  Spinner
} from '@chakra-ui/react';
import {
  faHomeLg,
  faAngleUp,
  faAngleDown,
  faPlay,
  faUserHeadset
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ButtonText, SearchInput } from '@morressier/alembic';
import { useQuery } from '@tanstack/react-query';
import { debounce } from 'lodash';
import React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  fetchHelpVideo,
  fetchHelpVideoUnauthenticated,
  fetchPageHelpContent,
  fetchPageHelpContentUnauthenticated,
  fetchPageHelpTopic,
  fetchPageHelpTopicUnauthenticated
} from 'src/api/queries';
import ContactUsProvider from 'src/pages/ContactUs/ContactUsProvider';
import SubmissionLoader from 'src/pages/Organization/Workspace/Submissions/SubmissionLoader';

const HelpCenterTopicDrawer = ({
  isOpen,
  setIsOpen,
  pageName,
  topicId,
  isLoggedIn,
  onHomeClick
}: {
  isOpen: boolean;
  setIsOpen: (arg1: boolean) => void;
  pageName: { pageName: string; title: string };
  topicId: string;
  isLoggedIn: boolean;
  onHomeClick?: () => void;
}) => {
  const drawerSize = useBreakpointValue({ base: 'full', md: 'xl' });
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedIndices, setExpandedIndices] = useState<number[]>([0]);
  const [selectedTopic, setSelectedTopic] = useState(topicId);
  const [isVideoLoading, setIsVideoLoading] = useState(false);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [isContactSupportOpen, setIsContactSupportOpen] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const debouncedSetSearchTerm = useMemo(
    () => debounce((value: string) => setSearchTerm(value), 1000),
    []
  );
  useEffect(
    () => () => debouncedSetSearchTerm.cancel(),
    [debouncedSetSearchTerm]
  );
  // Reset playback state when the user switches topics.
  useEffect(() => {
    setIsVideoPlaying(false);
    setIsVideoLoading(false);
  }, [selectedTopic]);

  const { data: pageHelpContentData } = useQuery({
    queryKey: ['page-help-content', pageName, isLoggedIn],
    queryFn: () =>
      isLoggedIn
        ? fetchPageHelpContent(pageName.pageName)
        : fetchPageHelpContentUnauthenticated(pageName.pageName)
  });
  const topics = pageHelpContentData?.topics ?? [];

  const activeTopicId =
    topics.find(t => t.id === selectedTopic || t.title === selectedTopic)?.id ??
    selectedTopic;
  const activeTopic = topics.find(
    t => t.id === selectedTopic || t.title === selectedTopic
  );
  const { data: topicHelpData, isFetching: isTopicFetching } = useQuery({
    queryKey: [
      'page-help-topic-content',
      searchTerm,
      activeTopicId,
      isLoggedIn
    ],
    queryFn: () =>
      isLoggedIn
        ? fetchPageHelpTopic(pageName.pageName, activeTopicId, searchTerm)
        : fetchPageHelpTopicUnauthenticated(
            pageName.pageName,
            activeTopicId,
            searchTerm
          ),
    keepPreviousData: true
  });
  const faqs = topicHelpData?.topic?.help?.faqs ?? [];
  const isLoadingFAQs = isTopicFetching || searchInput !== searchTerm;
  const topicVideo = topicHelpData?.topic?.help?.video;
  const hasActiveTopicData = topicHelpData?.topic?.id === activeTopicId;
  const isInitialTopicLoad = isTopicFetching && !hasActiveTopicData;

  // Fetch the playable URL on demand and only then load/play the video.
  const handleVideoPlay = async () => {
    const player = videoRef.current;
    if (!topicVideo?.id || !player || isVideoLoading) {
      return;
    }
    setIsVideoLoading(true);
    try {
      const video = isLoggedIn
        ? await fetchHelpVideo(pageName.pageName, activeTopicId, topicVideo.id)
        : await fetchHelpVideoUnauthenticated(
            pageName.pageName,
            activeTopicId,
            topicVideo.id
          );
      if (video.result?.url) {
        player.src = video.result?.url;
        setIsVideoPlaying(true);
        await player.play();
      }
    } finally {
      setIsVideoLoading(false);
    }
  };
  const suggestedTopics = topics.filter(topic => topic?.id !== activeTopicId);
  return (
    <>
      <Drawer
        isOpen={isOpen}
        placement="right"
        onClose={() => setIsOpen(false)}
        size={drawerSize}
      >
        <DrawerOverlay />
        <DrawerContent
          display="flex"
          flexDirection="column"
          sx={{ height: { base: '100dvh', md: 'auto' } }}
        >
          <DrawerCloseButton onClick={() => setIsOpen(false)} />
          <DrawerHeader></DrawerHeader>
          <DrawerBody overflowY="auto" flex="1" p={0}>
            <Flex direction={'column'} gap={3}>
              {/* Top */}
              <Flex justifyContent={'space-between'}>
                <Breadcrumb>
                  <BreadcrumbItem>
                    <ButtonText
                      leftIcon={<Icon as={FontAwesomeIcon} icon={faHomeLg} />}
                      onClick={() =>
                        onHomeClick ? onHomeClick() : setIsOpen(false)
                      }
                    >
                      Help Center
                    </ButtonText>
                  </BreadcrumbItem>
                  <BreadcrumbItem>
                    <Text textStyle={'textBody'}>{pageName?.title}</Text>
                  </BreadcrumbItem>
                </Breadcrumb>
                <ButtonText
                  leftIcon={<Icon as={FontAwesomeIcon} icon={faUserHeadset} />}
                  onClick={() => setIsContactSupportOpen(true)}
                  mr={3}
                >
                  Contact Support
                </ButtonText>
              </Flex>
              <Divider />

              <Flex direction="column" px={3}>
                <Text textStyle="headingSection">{activeTopic?.title}</Text>
                <Text textStyle="textContext">{activeTopic?.description}</Text>
              </Flex>

              {/* Video Layout */}
              {isInitialTopicLoad ? (
                <Flex
                  justifyContent="center"
                  alignItems="center"
                  direction="column"
                  gap={3}
                  py={8}
                >
                  <Spinner size="lg" color="blue.500" />
                  <Text textStyle="textContext">
                    {searchInput !== searchTerm
                      ? 'Searching...'
                      : 'Loading...'}
                  </Text>
                </Flex>
              ) : (
                topicVideo?.title && (
                  <Flex height={'330px'} px={3} direction={'column'} gap={1}>
                    <Text textStyle={'textContext'}>Watch</Text>
                    <Box position="relative" width="100%">
                      <video
                        key={activeTopicId}
                        ref={videoRef}
                        poster={topicVideo?.thumbnailUrl}
                        controls={isVideoPlaying}
                        controlsList="nodownload"
                        onEnded={() => setIsVideoPlaying(false)}
                        style={{
                          width: '100%',
                          height: '300px',
                          aspectRatio: isVideoPlaying ? undefined : '16 / 9',
                          objectFit: 'contain',
                          display: 'block',
                          boxShadow: '0 2px 6px rgba(0, 0, 0, 0.12)'
                        }}
                      />
                      {!isVideoPlaying && (
                        <Flex
                          position="absolute"
                          top={0}
                          left={0}
                          right={0}
                          bottom={0}
                          align="center"
                          justify="center"
                          cursor={topicVideo ? 'pointer' : 'default'}
                          onClick={handleVideoPlay}
                        >
                          {isVideoLoading ? (
                            <Spinner color="white" size="xl" />
                          ) : (
                            topicVideo && (
                              <Flex
                                align="center"
                                justify="center"
                                boxSize={'64px'}
                                borderRadius="full"
                                bg="rgba(0, 0, 0, 0.6)"
                              >
                                <Icon
                                  as={FontAwesomeIcon}
                                  icon={faPlay}
                                  color="white"
                                  boxSize={'24px'}
                                  ml={'4px'}
                                />
                              </Flex>
                            )
                          )}
                        </Flex>
                      )}
                    </Box>
                  </Flex>
                )
              )}
              <Box px={3}>
                <Flex align="baseline" gap={1} flexWrap="wrap">
                  <Text textStyle="textContext">Related Topics:</Text>
                  {suggestedTopics.map((topic, index) => (
                    <React.Fragment key={topic.title}>
                      <ButtonText
                        fontSize="sm"
                        onClick={() => setSelectedTopic(topic?.id)}
                      >
                        {topic.title}
                        {index < suggestedTopics.length - 1 && ','}
                      </ButtonText>
                    </React.Fragment>
                  ))}
                </Flex>
              </Box>

              <Divider />
              <Flex direction={'column'} gap={3} px={3} py={3}>
                <Text textStyle={'headingItem'}>
                  Frequently Asked Questions (FAQs)
                </Text>
                <SearchInput
                  placeholder="Search questions"
                  value={searchInput}
                  name="search"
                  onInputClear={() => {
                    debouncedSetSearchTerm.cancel();
                    setSearchInput('');
                    setSearchTerm('');
                  }}
                  onChange={e => {
                    setSearchInput(e.target.value);
                    debouncedSetSearchTerm(e.target.value);
                  }}
                />
                <Flex overflowY={'auto'} height={'100%'} direction={'column'}>
                  {isLoadingFAQs ? (
                    <Flex
                      justifyContent="center"
                      alignItems="center"
                      direction="column"
                      gap={3}
                      py={8}
                    >
                      <Spinner size="lg" color="blue.500" />
                      <Text textStyle="textContext">
                        {searchInput !== searchTerm
                          ? 'Searching...'
                          : 'Loading...'}
                      </Text>
                    </Flex>
                  ) : faqs && faqs.length > 0 ? (
                    <Accordion
                      width={'100%'}
                      allowMultiple
                      index={expandedIndices}
                      onChange={indices =>
                        setExpandedIndices(
                          Array.isArray(indices) ? indices : [indices]
                        )
                      }
                    >
                      <Flex gap={2} direction={'column'}>
                        {faqs.map(faq => (
                          <AccordionItem key={faq?.id}>
                            {({ isExpanded }) => (
                              <>
                                <AccordionButton
                                  px={2}
                                  py={1}
                                  bgColor="#F9FAFB"
                                  border={'1px solid #D0D5DD'}
                                  borderColor="neutralPrimaryBorder"
                                >
                                  <Flex
                                    justifyContent={'space-between'}
                                    alignItems={'center'}
                                    width={'100%'}
                                  >
                                    <Text
                                      textStyle={'headingItem'}
                                      textAlign={'left'}
                                    >
                                      {faq.question}
                                    </Text>
                                    <Flex gap={3} alignItems="center">
                                      <Box ml={2} p={2}>
                                        <FontAwesomeIcon
                                          icon={
                                            isExpanded ? faAngleUp : faAngleDown
                                          }
                                          color="blue"
                                        />
                                      </Box>
                                    </Flex>
                                  </Flex>
                                </AccordionButton>
                                <AccordionPanel
                                  px={4}
                                  py={3}
                                  border={'1px solid #D0D5DD'}
                                  borderColor="neutralPrimaryBorder"
                                  bgColor="primary"
                                >
                                  <Flex direction="column" gap={6}>
                                    <Box>
                                      <Text textStyle={'textContext'}>
                                        {faq.answer}
                                      </Text>
                                    </Box>
                                  </Flex>
                                </AccordionPanel>
                              </>
                            )}
                          </AccordionItem>
                        ))}
                      </Flex>
                    </Accordion>
                  ) : (
                    <SubmissionLoader
                      title="No Results Found"
                      subTitle={
                        'We could not find any results matching your search criteria. Please adjust your search and try again.'
                      }
                      bgColor="neutralSecondaryFill"
                    />
                  )}
                </Flex>
              </Flex>
            </Flex>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
      {isContactSupportOpen && (
        <ContactUsProvider
          isLoggedIn={isLoggedIn}
          isOpen={isContactSupportOpen}
          setIsOpen={setIsContactSupportOpen}
        />
      )}
    </>
  );
};

export default HelpCenterTopicDrawer;
